// @unocss-include
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, clsx, ConfirmedButton, H2, Icon, P, toast, } from '@a-type/ui';
import { appsById } from '@biscuits/apps';
import { useContext, useState } from 'react';
import { useAppId } from '../common/Context.js';
import { useHasServerAccess } from '../hooks/graphql.js';
import { VerdantContext } from '../verdant.js';
export function ExportDataButton(_a) {
    var { children, onError } = _a, props = __rest(_a, ["children", "onError"]);
    const [loading, setLoading] = useState(false);
    const clientDesc = useContext(VerdantContext);
    const appId = useAppId();
    const app = appsById[appId];
    if (!clientDesc)
        return null;
    return (_jsx(Button, Object.assign({ onClick: async () => {
            setLoading(true);
            try {
                const backup = await import('@verdant-web/store/backup');
                const client = await clientDesc.open();
                const file = await backup.createClientBackup(client);
                const url = URL.createObjectURL(file);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${app.name}-backup-${new Date().toISOString()}.zip`;
                a.click();
            }
            catch (e) {
                console.error(e);
                onError(e);
            }
            finally {
                setLoading(false);
            }
        }, loading: loading }, props, { children: children || (_jsxs(_Fragment, { children: ["Export Data ", _jsx(Icon, { name: "download" })] })) })));
}
export function ImportDataButton(_a) {
    var { children, onError } = _a, props = __rest(_a, ["children", "onError"]);
    const [loading, setLoading] = useState(false);
    const clientDesc = useContext(VerdantContext);
    if (!clientDesc)
        return null;
    return (_jsx(Button, Object.assign({ asChild: true, loading: loading }, props, { children: _jsxs("label", { children: [_jsx("input", { type: "file", hidden: true, accept: ".zip", onChange: async (ev) => {
                        var _a;
                        setLoading(true);
                        try {
                            const backup = await import('@verdant-web/store/backup');
                            const client = await clientDesc.open();
                            const file = (_a = ev.target.files) === null || _a === void 0 ? void 0 : _a[0];
                            if (!file) {
                                throw new Error('No file selected');
                            }
                            await backup.importClientBackup(client, file);
                            window.location.reload();
                        }
                        catch (e) {
                            console.error(e);
                            onError(e);
                        }
                        finally {
                            setLoading(false);
                        }
                    } }), children || (_jsxs(_Fragment, { children: ["Import Data ", _jsx(Icon, { name: "upload" })] }))] }) })));
}
function defaultOnError(error) {
    toast.error(error.message);
}
export function ManageStorage({ className, onError = defaultOnError, }) {
    const isSubscribed = useHasServerAccess();
    const clientDesc = useContext(VerdantContext);
    // sync users cannot manage local storage, it would cause unexpected
    // results...
    if (isSubscribed) {
        return (_jsxs("div", { className: clsx('flex flex-col items-start gap-2', className), children: [_jsx(H2, { children: "Manage Device Storage" }), _jsx(P, { className: "text-xs mb-2", children: "Your data is synced to Biscuits. If you're experiencing issues, you can try resetting your local device to the server's data." }), _jsx("div", { className: "flex flex-row gap-2 flex-wrap", children: _jsx(ConfirmedButton, { color: "destructive", confirmText: "This will delete your local data and replace it with the server's data. It cannot be undone.", onConfirm: () => {
                            clientDesc === null || clientDesc === void 0 ? void 0 : clientDesc.__dangerous__resetLocal();
                        }, children: "Reset local data" }) })] }));
    }
    return (_jsxs("div", { className: clsx('flex flex-col items-start gap-2', className), children: [_jsx(H2, { children: "Manage App Storage" }), _jsx(P, { className: "text-xs mb-2", children: "This app stores all data on your device. You can export and import your data, or reset it." }), _jsxs("div", { className: "flex flex-row gap-2 flex-wrap", children: [_jsx(ExportDataButton, { onError: onError }), _jsx(ImportDataButton, { onError: onError }), _jsx(ConfirmedButton, { color: "destructive", confirmText: "This will delete your data. It cannot be undone.", onConfirm: () => {
                            clientDesc === null || clientDesc === void 0 ? void 0 : clientDesc.__dangerous__resetLocal();
                        }, children: "Reset data" })] })] }));
}
//# sourceMappingURL=storage.js.map